import { useEffect } from "react";
import {  postSearchHotelsService } from "api.service";
import { useSearch } from "context/SearchContext";
import { useSearchResultFn } from "hooks/useSearchApi";
import { ISearchResult } from "interfaces/searchResult";
import { currentUrlParams } from "utils/getUrlParams";

import moment from "moment";
import { StepProps } from "components/Stepper/StepperTypes";
import BookingRooms from "containers/ListingDetailPage/BookingRooms";
import { useFetchHotelDetails } from "hooks/useFetchHotelDetails";
import { useParams } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export const Step2: React.FC<StepProps> = ({
  handleStepChange,
  selectedArrangements,
  setSelectedArrangements,
  selectedRooms,
  setSelectedRooms,
}) => {
  const hotelService = useSearchResultFn(postSearchHotelsService);

  const { hotelSlug }: { hotelSlug: string } = useParams();
  const {
    searchData,
    setSearchData,
    searchResult,
    setSearchResult,
    searchDestinations,
    getSearchPathWithQuery,
    myHotelInformation,
  } = useSearch();

  const { hotelDetails } = useFetchHotelDetails();
  const currentParams = currentUrlParams();

  useEffect(() => {
    if (searchResult?.occupancies.length !== 0) {
      const rooms = searchResult?.occupancies.reduce(
        (acc: any, val: any, index: any) => {
          const sortedByPrice = val.room_types.sort(
            (a: any, b: any) =>
              Number(a?.board?.[0].net) - Number(b?.board?.[0].net)
          );
          const firstSelectableRoomForSales = sortedByPrice.find(
            (el: any) => !el.stop_sale && el.quantity
          );
          const firstSelectableRoomOverbooked = sortedByPrice.find(
            (el: any) => !el.stop_sale && !el.quantity
          );
          const firstSelectableRoomStopsales = sortedByPrice.find(
            (el: any) => el.stop_sale
          );
          acc = {
            ...acc,
            [index]:
              firstSelectableRoomForSales?.data.id ||
              firstSelectableRoomOverbooked?.data.id ||
              firstSelectableRoomStopsales?.data.id,
          };

          return acc;
        },
        {}
      );
      setSelectedRooms(rooms);

      const arrangements = searchResult?.occupancies.reduce(
        (acc: any, val: any, index: any) => {
          acc = {
            ...acc,
            [index]: val.room_types.reduce((acc: any, roomType: any) => {
              acc = {
                ...acc,
                [roomType.data.id]: {
                  boardId: roomType.board[0].data.id,
                },
              };
              return acc;
            }, {}),
          };
          return acc;
        },
        {}
      );
      setSelectedArrangements(arrangements);
    }
  }, [searchResult]);

  useEffect(() => {
    if (currentParams.start) {
      const start_date = moment(currentParams.start);
      const end_date = moment(currentParams.end);
      const roomCount = Number(currentParams.rooms);

      // Create array of the occupancies based on params URL
      const occupancies = [];

      for (let i = 1; i <= roomCount; i++) {
        const adults = Number(currentParams[`adults${i}`] || 2);
        const childrenParam = currentParams[`children${i}`];

        const child_ages = childrenParam
          ? childrenParam
              .split(",")
              .map(Number)
              .filter((age: number) => !isNaN(age))
          : [];

        occupancies.push({
          adult: adults,
          child: child_ages.length,
          child_ages: child_ages,
        });
      }

      // Prepare les données pour setSearchData
      const rooms = occupancies.map((occ) => ({
        guestAdults: occ.adult,
        guestChildren: occ.child_ages,
      }));

      setSearchData((prev: any) => ({
        ...prev,
        dateRange: { startDate: start_date, endDate: end_date },
        rooms,
      }));

      hotelService
        .execute({
          hotel: hotelSlug,
          start_date: start_date.format("YYYY-MM-DD"),
          end_date: end_date.format("YYYY-MM-DD"),
          currency: searchData?.currency,
          occupancies,
        })
        .then((res: [ISearchResult]) => {
          setSearchResult(res[0]);
          setTimeout(() => {
            document
              .getElementById("search-room-result")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        });
    }
  }, []);

  const getSearchLink = () => {
    if (
      searchDestinations.hotels.find((el) => el.slug === searchData.hotel_slug)
    ) {
      return getSearchPathWithQuery("/hotel-detail/" + searchData.hotel_slug);
    } else if (myHotelInformation?.slug) {
      return getSearchPathWithQuery(
        "/hotel-detail/" + myHotelInformation?.slug
      );
    } else {
      return getSearchPathWithQuery("/hotels");
    }
  };

  const onSelectRoomType = (roomIndex: number) => (roomTypeId: number) => {
    setSelectedRooms((v) => ({
      ...v,
      [roomIndex]: roomTypeId,
    }));
  };

  const Booking = () => {
    return (
      <div className="listingSection__wrap lg:border-b border-b-0 p-0 border-none">
        {searchResult?.occupancies?.map((room: any, index: any) => (
          <div key={index}>
            <h2 className="text-black capitalize lg:text-xl text-base">
              Chambre {index + 1} : {room.pax.adult} Adults {room?.pax?.child > 0 && `et ${room.pax?.child} enfants`}
            </h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-5" />
            {room.room_types.length !== 0 ? (
              <BookingRooms
                isHotelDetails
                hotelDetails={hotelDetails}
                selectedRoomType={selectedRooms?.[index]}
                onSelectRoomType={onSelectRoomType(index)}
                roomIndex={index}
                selectedArrangements={selectedArrangements}
                setSelectedArrangements={setSelectedArrangements}
                roomTypes={room.room_types}
                isIframe
              />
            ) : (
              <div className="text-base text-red-500">
                Aucune chambre disponible correspondante à votre recherche.
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {searchResult && <Booking />}
      <div className="flex justify-end">
        <ButtonPrimary
        className="rounded-none !bg-[#aa8453]"
        onClick={handleStepChange}
      >
        Suivant
      </ButtonPrimary>
      </div>
    </div>
  );
};

export default Step2;
